import React, { useState } from 'react'
import { DatePicker, Select, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import dayjs from 'dayjs'
import moment, { Moment } from 'moment'
import Button from '@/components/button/button'
import $msg from '@/utils/message'
import { getMemberCardRecords } from '../api'
import SettingPage, { SettingTableContainer } from '../member-setting/setting-page'
import { getMemberTablePagination, getTableTotal, MEMBER_TABLE_PAGE_SIZE } from '../table-utils'
import { getMemberCardSceneText, MEMBER_CARD_SCENE_OPTIONS } from './member-card-record-utils'

const MemberCardRecord: React.FC = () => {
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(MEMBER_TABLE_PAGE_SIZE)
    const [sceneList, setSceneList] = useState<number[]>([])
    const [date, setDate] = useState<string[]>([
        dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
        dayjs().format('YYYY-MM-DD'),
    ])
    const [dateValue, setDateValue] = useState<[Moment | null, Moment | null] | null>([
        moment().subtract(1, 'month'),
        moment(),
    ])
    const resetFilter = () => {
        setPage(1)
        setSceneList([])
        setDate([dayjs().subtract(1, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')])
        setDateValue([moment().subtract(1, 'month'), moment()])
    }

    const req = useRequest(
        () =>
            getMemberCardRecords({
                page,
                limit: pageSize,
                from_date: date[0],
                to_date: date[1],
                scene_list: sceneList.length ? sceneList : undefined,
            }),
        {
            refreshDeps: [page, pageSize, date, sceneList],
            onError: e => $msg('error', e.message || '获取领卡记录失败'),
        },
    )

    const columns: ColumnsType<any> = [
        {
            title: '领卡时间',
            dataIndex: 'create_time',
            width: 170,
            render: value => dayjs(value).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: '会员',
            dataIndex: 'member',
            width: 180,
            render: value => value?.name || value?.phone || '-',
        },
        {
            title: '手机号',
            dataIndex: 'member',
            width: 140,
            render: value => value?.phone || '-',
        },
        {
            title: '领卡场景',
            dataIndex: 'scene',
            width: 160,
            render: value => <span className="member-tag">{getMemberCardSceneText(value)}</span>,
        },
        {
            title: '邀请人',
            dataIndex: 'inviter',
            width: 160,
            render: value => value?.name || value?.phone || '-',
        },
    ]

    return (
        <SettingPage
            title="领卡记录"
            className="member-basic-setting"
            bodyClassName="member-basic-setting__body"
            extra={
                <>
                    <DatePicker.RangePicker
                        dateRender={current => (
                            <div className="ant-picker-cell-inner" style={{ borderRadius: '50%' }}>
                                {current.date()}
                            </div>
                        )}
                        value={dateValue}
                        allowClear
                        suffixIcon={<i className="iconfont iconshijianshaixuan" />}
                        onChange={(momentValue, dateString) => {
                            setPage(1)
                            setDateValue(momentValue)
                            setDate(dateString[0] ? dateString : [])
                        }}
                    />
                    <Select
                        mode="multiple"
                        allowClear
                        placeholder="领卡场景"
                        options={MEMBER_CARD_SCENE_OPTIONS}
                        value={sceneList}
                        style={{ width: 260 }}
                        onChange={values => {
                            setPage(1)
                            setSceneList(values)
                        }}
                    />
                    <Button types="primary" onClick={resetFilter}>
                        重置
                    </Button>
                    <Button types="primary" loading={req.loading} onClick={() => req.refresh()}>
                        刷新
                    </Button>
                </>
            }
        >
            <SettingTableContainer className="member-basic-setting__panel">
                <Table
                    bordered
                    rowKey={(record, index) => `${record.id}-${index}`}
                    loading={req.loading}
                    columns={columns}
                    dataSource={req.data?.records || []}
                    pagination={getMemberTablePagination({
                        current: page,
                        pageSize,
                        total: getTableTotal(req.data?.pagination),
                        onChange: setPage,
                        onShowSizeChange: (nextPage, nextSize) => {
                            setPage(nextPage)
                            setPageSize(nextSize)
                        },
                    })}
                    scroll={{ x: 'max-content' }}
                />
            </SettingTableContainer>
        </SettingPage>
    )
}

export default MemberCardRecord
